import { ChevronDown } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Box } from '@/components/ui/v3/Box';
import { ColorVariant, Dropdown } from '@/components/ui/v3/Dropdown';
import { Tooltip } from '@/components/ui/v3/Tooltip';
import { useMatter } from '@/hooks/useMatter';
import { toTitleCase } from '@/lib/strings';
import { useUpdateMedicalProviderMutation } from '@/services/api/treatmentService';
import {
  MedicalProvider,
  ProviderTreatmentStatus,
} from '@/services/types/treatment-types';

import {
  formatProviderTreatmentDates,
  getProviderTreatmentDates,
} from '../utils';
import { ContactCardCompact } from './ContactCard';
import { RequestStatusSummary } from './RequestStatusSummary';

interface ProviderSummaryProps {
  provider: MedicalProvider;
}
const SubheadingItem = ({ label, value }: { label: string; value: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (value === 'Not provided') return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`Copied ${label.toLowerCase().replace(':', '')}`);
      setTimeout(() => setCopied(false), 1000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const isCopyable = value !== 'Not provided';

  return (
    <div className="flex flex-row gap-1.5">
      <span className="text-xs font-semibold text-gray-500 flex-shrink-0">
        {label}
      </span>
      {isCopyable ? (
        <Tooltip content="Click to copy" variant="dark" side="top">
          <button
            type="button"
            onClick={handleCopy}
            className={`text-xs text-left cursor-pointer transition-colors underline decoration-dotted underline-offset-2 ${
              copied
                ? 'text-green-600 font-semibold'
                : 'text-gray-700 hover:text-blue-600'
            }`}
          >
            {value}
          </button>
        </Tooltip>
      ) : (
        <span className="text-xs text-gray-500 cursor-default">{value}</span>
      )}
    </div>
  );
};

const ProviderInformation = ({ provider }: { provider: MedicalProvider }) => {
  const navigate = useNavigate();
  const { matterId } = useMatter();
  const { startingDate, endingDate } = useMemo(
    () => getProviderTreatmentDates(provider),
    [provider],
  );

  const handleViewProvider = () => {
    navigate(`/matters/${matterId}/treatment/${provider.id}`);
  };

  return (
    <div className="flex flex-col gap-2.5 @container w-full">
      <div className="flex items-center gap-2">
        <Tooltip
          content={`View details for ${provider.name || 'Unnamed Provider'}`}
          variant="dark"
          side="top"
        >
          <button
            type="button"
            onClick={handleViewProvider}
            className="text-md font-semibold hover:text-gray-700 cursor-pointer underline decoration-dotted underline-offset-4 text-left truncate"
          >
            {provider.name || 'Unnamed Provider'}
          </button>
        </Tooltip>
      </div>
      <div className="grid grid-cols-[1.5fr_1fr] gap-6">
        {/* Left Column: Provider Info */}
        <div className="flex flex-col gap-1.5">
          <SubheadingItem
            label="Dates:"
            value={(() => {
              const dates = formatProviderTreatmentDates(
                provider.treatment_status,
                startingDate,
                endingDate,
              );
              return dates === 'Unknown' ? 'Not provided' : dates;
            })()}
          />
          <SubheadingItem
            label="Physician:"
            value={
              provider.practitioner_names &&
              provider.practitioner_names.length > 0
                ? provider.practitioner_names.join(', ')
                : 'Not provided'
            }
          />
          <SubheadingItem
            label="Address:"
            value={provider.address || 'Not provided'}
          />
          <SubheadingItem
            label="Number of Visits:"
            value={provider.encounters?.length?.toString() ?? 'Not provided'}
          />
        </div>
        {/* Right Column: Contact Info */}
        <div className="flex flex-col gap-1.5">
          <ContactCardCompact handles={provider.handles} />
        </div>
      </div>
    </div>
  );
};

const ProviderStatus = ({ provider }: { provider: MedicalProvider }) => {
  const { matterId } = useMatter();
  const [updateProvider] = useUpdateMedicalProviderMutation();
  const statusToColor: Record<ProviderTreatmentStatus, ColorVariant> = {
    [ProviderTreatmentStatus.TREATING]: 'purple',
    [ProviderTreatmentStatus.COMPLETED]: 'green',
    [ProviderTreatmentStatus.REFERRED_TO]: 'blue',
    [ProviderTreatmentStatus.DEVIATING]: 'red',
    [ProviderTreatmentStatus.UNKNOWN]: 'gray',
    [ProviderTreatmentStatus.NEVER_TREATED]: 'red',
  };

  return (
    <div className="flex flex-row gap-2 items-center">
      <span className="text-xs font-semibold text-gray-500">Status:</span>
      <Dropdown>
        <Dropdown.Button
          variant={statusToColor[provider.treatment_status]}
          className="flex flex-row gap-1.5 items-center justify-center text-xs py-1 px-2"
        >
          <div className="text-xs">
            {toTitleCase(provider.treatment_status)}
          </div>
          <ChevronDown className="w-3 h-3" />
        </Dropdown.Button>
        <Dropdown.Items>
          {Object.values(ProviderTreatmentStatus).map((status) => (
            <Dropdown.Item
              key={status}
              onClick={() => {
                updateProvider({
                  matterId,
                  providerId: provider.id,
                  provider: {
                    treatment_status: status,
                  },
                });
              }}
            >
              <span>{toTitleCase(status)}</span>
            </Dropdown.Item>
          ))}
        </Dropdown.Items>
      </Dropdown>
    </div>
  );
};

export const ProviderSummary = ({ provider }: ProviderSummaryProps) => {
  return (
    <Box className="flex flex-col gap-6 p-6">
      <div className="flex flex-row gap-6 justify-between items-start w-full">
        <ProviderInformation provider={provider} />
        <div className="flex flex-row gap-3 items-start">
          <ProviderStatus provider={provider} />
        </div>
      </div>
      <RequestStatusSummary provider={provider} />
    </Box>
  );
};

export const ProviderSummarySkeleton = () => {
  return (
    <div className="flex flex-col gap-4 animate-pulse">
      <div className="h-6 bg-gray-200 rounded w-1/3" />
      <div className="h-20 bg-gray-100 rounded" />
      <div className="h-16 bg-gray-100 rounded" />
    </div>
  );
};
